/**
 * The five sections, and the boxes an operator corrects them in.
 *
 * EVERY LINE IS EDITABLE AND THE EDITED TEXT IS WHAT THE DIALER READS. That is
 * the whole point of the tab: a model wrote sentences about a company it has
 * never spoken to, and the person about to say them out loud is the one who
 * knows which of them are wrong. A tab that could only regenerate would leave
 * them retyping the same fix after every run.
 *
 * FOUR FIELDS ARE DELIBERATELY NOT EDITABLE HERE and the server refuses them
 * too: the finding ids under a line, the checklist id on a question, the source
 * disclosure, and the package prices. They are what make a claim provable, a
 * question countable, a disclosure lawful and a price true.
 *
 * The `unproven` badge is a WARNING, not a deletion. The server marks a line
 * whose evidence a corrected rule has retired and leaves the sentence standing,
 * because the operator may have written that sentence themselves.
 *
 * A HOOK IS TWO BOXES, not one. The defect is only half a hook: the moment it is
 * said, the person on the other end wants to know what we would do about it, and
 * an operator hunting for that answer in a paragraph they wrote yesterday has
 * already lost the call. The remedy box is offered on every hook even when the
 * script predates the field, so an old script can be finished by hand.
 */
import { AlertTriangle, Link2, MinusCircle, Wrench } from "lucide-react";
import { StatusPill } from "../../../../ui";
import type { T } from "../../../site-scanner/types";
import type { BrainFinding } from "../../../../../lib/siteBrain";
import {
  FIX_EDIT_SUFFIX,
  type BrainCallScript,
  type BrainScriptLine,
  type BrainScriptProgress,
  type BrainScriptSection,
} from "../../../../../lib/siteBrainScript";
import { findingText } from "../findingText";

/** Roughly how tall each section's boxes want to be. Opening is one breath. */
const ROWS: Record<BrainScriptSection["id"], number> = {
  opening: 3,
  hooks: 2,
  questions: 2,
  objections: 3,
  close: 2,
};

export function ScriptSections({
  script,
  drafts,
  canManage,
  onDraft,
  t,
}: {
  script: BrainCallScript;
  drafts: Record<string, string>;
  canManage: boolean;
  onDraft: (id: string, text: string) => void;
  t: T;
}) {
  return (
    <div className="space-y-4">
      {script.sections.map((section) => (
        <section key={section.id} className="space-y-2">
          <div className="flex flex-wrap items-baseline gap-2">
            <h3 className="text-sm font-semibold">{t(`brain.script.section.${section.id}`)}</h3>
            <span className="text-xs text-muted-foreground">{t(`brain.script.hint.${section.id}`)}</span>
          </div>

          {section.lines.map((line) => (
            <Line
              key={line.id}
              line={line}
              drafts={drafts}
              rows={ROWS[section.id]}
              withFix={section.id === "hooks"}
              canManage={canManage}
              onDraft={onDraft}
              t={t}
            />
          ))}

          {/* Under the opening, never inside it. The server owns this sentence,
              so an operator editing the words above cannot delete the one that
              makes ringing a stranger's own phone defensible. */}
          {section.id === "opening" && script.sourceLine ? (
            <p className="rounded-xl bg-[var(--crm-sunk)] px-3 py-2 text-xs leading-relaxed text-muted-foreground">
              {script.sourceLine}
              <span className="mt-1 block opacity-70">{t("brain.script.sourceLineWhy")}</span>
            </p>
          ) : null}

          {/* The same arrangement, the opposite obligation: the sentence above is
              owed to the person being rung, this one is a gift to the operator.
              Under the opening because it is what a second call opens WITH, and
              outside the editable boxes because the server is the only thing that
              can prove a repair happened. */}
          {section.id === "opening" && script.progress ? (
            <Progress progress={script.progress} t={t} />
          ) : null}
        </section>
      ))}
    </div>
  );
}

/**
 * What they have fixed since we last spoke.
 *
 * ⚠ THESE FINDINGS ARE NOT CURRENT DEFECTS. Every one of them was measured, then
 * measured again and found gone, so they are drawn in the success colour with a
 * struck-out mark and never with the severity palette the defect list uses. An
 * operator who reads this box as a defect list opens the call by telling somebody
 * their site is broken in the exact places they have just repaired.
 *
 * The sentences come from `findingText`, the same helper the findings tab and the
 * history tab use, so a repair is described in the words the defect was described
 * in. The address is under each one, because two dead links produce the same
 * catalogue sentence and naming the address is most of what makes the observation
 * land.
 */
function Progress({ progress, t }: { progress: BrainScriptProgress; t: T }) {
  return (
    <div
      className="space-y-1.5 rounded-xl bg-[var(--crm-sunk)] px-3 py-2"
      style={{ boxShadow: "inset 3px 0 0 0 color-mix(in oklab, var(--color-success) 70%, transparent)" }}
    >
      <p className="text-xs font-semibold">{t("brain.script.progress.title", { n: progress.fixed.length })}</p>

      <ul className="space-y-0.5">
        {progress.fixed.map((finding: BrainFinding) => (
          <li key={finding.id} className="flex items-start gap-1.5 text-xs">
            <MinusCircle className="mt-0.5 size-3 shrink-0" style={{ color: "var(--color-success)" }} />
            <div className="min-w-0 flex-1">
              <p className="leading-snug">{findingText(finding, t)}</p>
              {finding.evidence.url ? (
                <p className="truncate text-muted-foreground">{finding.evidence.url}</p>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      {/* The second half, and the reason this box is not just a compliment: the
          operator has opened warmly and now needs somewhere to go. */}
      {progress.stayed > 0 ? (
        <p className="text-xs text-muted-foreground">{t("brain.script.progress.stayed", { n: progress.stayed })}</p>
      ) : null}

      <p className="text-xs opacity-70">{t("brain.script.progress.why")}</p>
    </div>
  );
}

function Line({
  line,
  drafts,
  rows,
  withFix,
  canManage,
  onDraft,
  t,
}: {
  line: BrainScriptLine;
  drafts: Record<string, string>;
  rows: number;
  /** Hooks only: the second half, what we would do about the defect. */
  withFix: boolean;
  canManage: boolean;
  onDraft: (id: string, text: string) => void;
  t: T;
}) {
  const value = drafts[line.id] ?? line.text;
  const changed = value !== line.text;

  return (
    <div className="space-y-1">
      {line.cue ? (
        <p className="text-xs font-medium text-muted-foreground">
          {t("brain.script.whenTheySay", { cue: line.cue })}
        </p>
      ) : null}

      {canManage ? (
        <textarea
          value={value}
          rows={rows}
          onChange={(e) => onDraft(line.id, e.target.value)}
          className="w-full resize-y rounded-xl border border-border bg-card/60 px-3 py-2 text-sm leading-relaxed outline-none focus:border-primary"
        />
      ) : (
        <p className="rounded-xl border border-border bg-card/60 px-3 py-2 text-sm leading-relaxed">{value}</p>
      )}

      {withFix ? <Fix line={line} drafts={drafts} canManage={canManage} onDraft={onDraft} t={t} /> : null}

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {changed ? <StatusPill tone="wt">{t("brain.script.unsaved")}</StatusPill> : null}

        {/* A line whose proof the run no longer has. Shown, never removed - the
            sentence may be the operator's own. */}
        {line.unproven ? (
          <span className="inline-flex items-center gap-1" style={{ color: "var(--color-warning)" }}>
            <AlertTriangle className="size-3 shrink-0" />
            {t("brain.script.unproven")}
          </span>
        ) : null}

        {line.findingIds?.length ? (
          <span className="inline-flex items-center gap-1">
            <Link2 className="size-3 shrink-0" />
            {t("brain.script.leansOn", { n: line.findingIds.length })}
          </span>
        ) : null}

        {line.checklistId ? <StatusPill tone="mut">{line.checklistId}</StatusPill> : null}
      </div>
    </div>
  );
}

/**
 * The second half of a hook: what we would do about the defect above it.
 *
 * Sunk rather than bordered, so a glance tells the two apart mid-call: the claim
 * is what the operator says, this is the answer they reach for when the reply is
 * "and then what". Saved under a suffixed id (`hook-1:fix`), which is why the
 * draft key is built here rather than passed in.
 *
 * An EMPTY box is still shown to somebody who may edit. A script generated
 * before this field existed has no remedy at all, and hiding the box would leave
 * the operator with no way to write one.
 */
function Fix({
  line,
  drafts,
  canManage,
  onDraft,
  t,
}: {
  line: BrainScriptLine;
  drafts: Record<string, string>;
  canManage: boolean;
  onDraft: (id: string, text: string) => void;
  t: T;
}) {
  const id = `${line.id}${FIX_EDIT_SUFFIX}`;
  const stored = line.fix ?? "";
  const value = drafts[id] ?? stored;

  if (!canManage && !value) return null;

  return (
    <div className="space-y-1 rounded-xl bg-[var(--crm-sunk)] px-3 py-2">
      <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Wrench className="size-3 shrink-0" />
        {t("brain.script.fixLabel")}
      </p>

      {canManage ? (
        <textarea
          value={value}
          rows={2}
          placeholder={t("brain.script.fixPlaceholder")}
          onChange={(e) => onDraft(id, e.target.value)}
          className="w-full resize-y rounded-lg border border-border bg-card/60 px-3 py-2 text-sm leading-relaxed outline-none focus:border-primary"
        />
      ) : (
        <p className="text-sm leading-relaxed">{value}</p>
      )}

      {value !== stored ? <StatusPill tone="wt">{t("brain.script.unsaved")}</StatusPill> : null}
    </div>
  );
}
